// AGC: DesktopSidebar - Left sidebar shell for desktop browsers (>= 1024px, mouse primary).
// AGC: Rendered by MainMenu when useIsDesktopLayout() returns true.
// AGC: Shares nav items (useMainNavItems) and mic gesture (useMicPressGesture) with BottomNav
// AGC: so behavior cannot drift between shells.
// AGC: Full nav: tab items (dashboard/lots/tasks/weather) + farm items (finances/inventory/machinery/team)
import React from 'react'
import { Menu, Bell, Mic, DollarSign, Package, Wrench, Users } from 'lucide-react'
import { useTheme } from '../../context/ThemeContext'
import { useTranslation } from '../../hooks/useTranslation'
import { usePermissions } from '../../hooks/usePermissions'
import { useMicPressGesture } from '../../hooks/useMicPressGesture'
import { useMainNavItems } from './mainNavItems'
import type { NavTab } from '../../types'
import type { Screen } from '../../types'

interface DesktopSidebarProps {
  active: NavTab
  onNavigate: (tab: NavTab) => void
  // AGC: For farm sub-pages (finances, inventory, machinery) that use Screen type not NavTab
  onNavigateScreen: (screen: Screen) => void
  onMicPress?: () => void
  onMicLongPress?: () => void
  onMicLongPressEnd?: () => void
  onMenuClick: () => void            // AGC: Opens AppDrawer (account only)
  onNotificationsClick: () => void
  notificationCount?: number
  // AGC: Currently active Screen — used to highlight farm section items
  activeScreen: Screen
}

/**
 * AGC: DesktopSidebar - Vertical sidebar for desktop browser layout.
 * AGC: Features:
 * AGC:  - Hamburger + logo at top (hamburger opens AppDrawer for account/settings)
 * AGC:  - Main nav section: Início, Lotes, Tarefas, Clima (permission-gated via useMainNavItems)
 * AGC:  - Farm section: Financeiro, Inventário, Maquinário, Equipe (permission-gated via usePermissions)
 * AGC:  - Inline mic button (same press/long-press UX as BottomNav floating button)
 * AGC:  - Notifications bell at bottom with unread badge
 * AGC:  - Full keyboard navigation with visible focus ring
 */
const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  active,
  onNavigate,
  onNavigateScreen,
  onMicPress,
  onMicLongPress,
  onMicLongPressEnd,
  onMenuClick,
  onNotificationsClick,
  notificationCount = 0,
  activeScreen,
}) => {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const permissions = usePermissions()
  const navItems = useMainNavItems()

  const { isMicActive, isLongPressing, handlers: micHandlers } = useMicPressGesture({
    onPress: onMicPress,
    onLongPress: onMicLongPress,
    onLongPressEnd: onMicLongPressEnd,
  })

  // AGC: Farm section items — permission-gated, navigate via onNavigateScreen (Screen type)
  const farmItems = [
    permissions.canViewFinancial
      ? { id: 'finances' as Screen, label: t('nav.finances'), icon: <DollarSign className="w-5 h-5" /> }
      : null,
    { id: 'inventory' as Screen, label: t('nav.inventory'), icon: <Package className="w-5 h-5" /> },
    { id: 'machinery' as Screen, label: t('nav.machinery'), icon: <Wrench className="w-5 h-5" /> },
    { id: 'team-users' as Screen, label: t('nav.team'), icon: <Users className="w-5 h-5" /> },
  ].filter(Boolean) as { id: Screen; label: string; icon: React.ReactNode }[]

  // AGC: Shared button renderer for sidebar nav items
  const renderNavButton = (
    id: string,
    label: string,
    icon: React.ReactNode,
    isActive: boolean,
    onClick: () => void
  ) => (
    <button
      key={id}
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
        transition-all duration-200
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-yellow-400
        ${isActive
          ? 'text-white bg-white/20'
          : 'text-white/60 hover:text-white/80 hover:bg-white/10 active:bg-white/30'
        }
      `.replace(/\s+/g, ' ').trim()}
      aria-current={isActive ? 'page' : undefined}
      title={label}
    >
      <span className="flex-shrink-0">{icon}</span>
      <span className="text-sm font-medium truncate">{label}</span>
      {isActive && (
        <span
          className="ml-auto w-1.5 h-1.5 rounded-full shadow-lg flex-shrink-0"
          style={{ backgroundColor: '#FFDF00', boxShadow: '0 0 6px rgba(255,223,0,0.5)' }}
        />
      )}
    </button>
  )

  return (
    <aside
      className="h-screen sticky top-0 flex flex-col z-30 select-none transition-colors"
      style={{
        backgroundColor: isDark ? '#0f172a' : '#065F46',
        width: '240px',
      }}
      aria-label="Sidebar navigation"
    >
      {/* AGC: Top — hamburger (opens account drawer) + logo */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-white/10">
        <button
          onClick={onMenuClick}
          className="
            flex items-center justify-center w-9 h-9 rounded-lg
            text-white/70 hover:text-white hover:bg-white/10
            active:scale-95 transition-all duration-200
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-yellow-400
          "
          aria-label="Open account menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* AGC: Dual logo — light/dark mode (matches AppHeader) */}
        <img
          src="/topmenulogo.png"
          alt="DroidFarm"
          className="h-7 w-auto object-contain dark:hidden"
        />
        <img
          src="/logologin.png"
          alt="DroidFarm"
          className="h-7 w-auto object-contain hidden dark:block"
        />
      </div>

      {/* AGC: Main nav + farm nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1" aria-label="Main navigation">
        {/* AGC: Tab nav items: Início, Lotes, Tarefas, Clima */}
        {navItems
          .filter(item => item.enabled)
          .map(item =>
            renderNavButton(
              item.id,
              item.label,
              item.icon,
              active === item.id,
              () => onNavigate(item.id)
            )
          )}

        {/* AGC: Divider between tab-nav and farm-nav */}
        {farmItems.length > 0 && (
          <div className="border-t border-white/10 my-2" />
        )}

        {/* AGC: Farm section: Financeiro, Inventário, Maquinário, Equipe */}
        {farmItems.map(item =>
          renderNavButton(
            item.id,
            item.label,
            item.icon,
            activeScreen === item.id,
            () => onNavigateScreen(item.id)
          )
        )}
      </nav>

      {/* AGC: Mic button — inline full-width, same UX as BottomNav floating button */}
      <div className="px-2 py-2 border-t border-white/10">
        <button
          {...micHandlers}
          className={`
            w-full flex items-center justify-center gap-2 py-3 rounded-xl
            transition-all duration-300 ease-out
            select-none touch-none
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-yellow-400
            ${isMicActive
              ? 'scale-95 opacity-90'
              : isLongPressing
                ? 'bg-red-500 shadow-xl shadow-red-500/25'
                : 'hover:scale-[1.02] active:scale-95'
            }
          `.replace(/\s+/g, ' ').trim()}
          style={{
            backgroundColor: isMicActive
              ? '#F59E0B'
              : isLongPressing
                ? undefined
                : isDark
                  ? '#22c55e'
                  : '#FFDF00',
          }}
          aria-label={t('common.voiceCommand')}
        >
          <Mic
            className={`
              w-5 h-5 transition-all duration-300
              ${isMicActive
                ? 'scale-90 text-white'
                : isLongPressing
                  ? 'scale-110 animate-pulse text-white'
                  : `scale-100 ${isDark ? 'text-white' : 'text-slate-800'}`
              }
            `.replace(/\s+/g, ' ').trim()}
          />
          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {t('common.voiceCommand')}
          </span>

          {/* AGC: Tap animation ring */}
          {isMicActive && (
            <span
              className="absolute inset-0 rounded-xl border-2 animate-ping"
              style={{ animationDuration: '1s', borderColor: 'var(--accent-light-bg)' }}
            />
          )}

          {/* AGC: Recording indicator ring */}
          {isLongPressing && (
            <span
              className="absolute inset-0 rounded-xl border-2 border-white animate-pulse"
              style={{ animationDuration: '0.5s' }}
            />
          )}
        </button>
      </div>

      {/* AGC: Bottom — notifications bell */}
      <div className="px-2 pb-4 pt-2 border-t border-white/10">
        <button
          onClick={onNotificationsClick}
          className="
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
            text-white/60 hover:text-white/80 hover:bg-white/10
            active:bg-white/30 transition-all duration-200
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-yellow-400
            relative
          "
          aria-label={`Notifications${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`}
        >
          <span className="relative flex-shrink-0">
            <Bell className="w-5 h-5" />
            {notificationCount > 0 && (
              <span className="
                absolute -top-1.5 -right-1.5
                min-w-[16px] h-[16px]
                flex items-center justify-center
                bg-red-500 text-white
                text-[10px] font-semibold
                rounded-full px-0.5
              ">
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </span>
          <span className="text-sm font-medium">
            {t('nav.notifications') ?? 'Notifications'}
          </span>
        </button>
      </div>
    </aside>
  )
}

export default DesktopSidebar
